import { Component, ElementRef, HostBinding, HostListener, OnDestroy, AfterViewInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { ChartConfiguration, ChartData, ChartOptions } from 'chart.js';
import { forkJoin, Subscription } from 'rxjs';
import { Chart } from 'angular-highcharts';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';
import { DashboardService } from 'src/app/shared/service/dashboard/dashboard.service';
import * as L from 'leaflet';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { getTcpBenchmarkBarColors } from 'src/app/shared/helpers/chart-palette.util';
import {
  applyUsernameClientFilter,
  extractSegmentOptionsFromRecords,
  matchesAnyClient,
  matchesPortCallDateRange,
  matchSegmentFilter,
  resolveRecordDate,
} from 'src/app/shared/helpers/pc-filter-options.util';
import {
  calculateUniqueVoyages,
  formatSummaryDisplay,
  getActualConsumptionMt,
  getVoyageKey,
  toSafeNumber,
} from 'src/app/shared/helpers/port-database-summary.util';

interface LegComparisonRow {
  key: string;
  label: string;
  icon: string;
  actual: number | null;
  warranted: number | null;
  optimized: number | null;
  isTotal?: boolean;
}

type LegComparisonMode = 'warranted' | 'optimization';
type ComplianceLegFilter = 'all' | 'maneuvering' | 'idle' | 'cargo';

interface FleetPerformanceRow {
  rank: number;
  shipName: string;
  segment: string;
  actualMt: number | null;
  warrantedMt: number | null;
  differenceMt: number | null;
  differencePct: number | null;
  status: 'Better' | 'Worse' | 'Neutral';
  isAverage?: boolean;
}

interface ComplianceSlice {
  key: 'achieved' | 'partial' | 'exceeded';
  label: string;
  count: number;
  percent: number;
  color: string;
  tooltip?: any;
}
interface ComplianceApiItem {
  achieved_target: number;
  achieved_target_percentage: number;
  exceeded_target: number;
  exceeded_target_percentage: number;
  partially_followed: number;
  partially_followed_percentage: number;
}

interface ComplianceApiResponse {
  cargo_ops: ComplianceApiItem;
  idle: ComplianceApiItem;
  maneuvering: ComplianceApiItem;
  overall: ComplianceApiItem;
  total_voyage: number;
}

@Component({
  selector: 'app-port-dashboard',
  templateUrl: './port-dashboard.component.html',
  styleUrls: ['./port-dashboard.component.scss'],
})
export class PortDashboardComponent implements OnDestroy, AfterViewInit {
  /** Legacy home UI — set true to show previous Home Page; false = new dashboard. */
  readonly showLegacyUi = false;

  // ------------ New home dashboard (reference design) ------------
  isLoadingConsumption = true;
  isLoadingCompliance = true;
  isLoadingPrediction = true;
  /** Fleet Performance is inactive on the approved Home UI — keep flag false. */
  isLoadingFleet = false;
  actualConsumptionMt: number | null = null;
  optimizedConsumptionMt: number | null = null;
  fuelSavedMt: number | null = null;
  fuelSavedVsDesignPct: number | null = null;
  lastUpdatedText = '--';
  legComparisonRows: LegComparisonRow[] = [];
  legComparisonUnit = 'MT';
  legComparisonMode: LegComparisonMode = 'warranted';
  complianceChart?: Chart;
  complianceTotalVoyages = 0;
  complianceSlices: ComplianceSlice[] = [];
  complianceLegFilter: ComplianceLegFilter = 'all';
  readonly complianceLegOptions: Array<{ value: ComplianceLegFilter; label: string }> = [
    { value: 'all', label: 'All Legs' },
    { value: 'maneuvering', label: 'Maneuvering' },
    { value: 'idle', label: 'Idle' },
    { value: 'cargo', label: 'Cargo Operations' },
  ];
  private complianceSourceRecords: any[] = [];
  /** Highcharts line chart: monthly Actual vs Optimization Prediction. */
  predictionChart?: Chart;
  /**
   * Fleet-level prediction KPIs shown beside the chart.
   * avgError/mape: mean |actual−predicted|/actual×100 over matched rows
   * accuracy: max(0, 100 − mape)
   * completeness: matchedRows / totalRows × 100
   */
  predictionKpis = {
    avgError: null as number | null,
    mape: null as number | null,
    accuracy: null as number | null,
    completeness: null as number | null,
  };
  /** Latest Month / Actual Trend / Latest Gap chips under the KPI grid. */
  predictionTrendSummary: Array<{
    label: string;
    value: string;
    tone: 'positive' | 'negative' | 'info' | 'neutral';
  }> = [];
  private dashboardResizeObserver?: ResizeObserver;
  private chartResizeTimer: ReturnType<typeof setTimeout> | null = null;
  fleetPerformanceRows: FleetPerformanceRow[] = [];
  fleetAverageRow: FleetPerformanceRow | null = null;
  selectedFleetSegment = 'All Segments';
  fleetSegmentOptions: string[] = ['All Segments'];
  fleetSegmentSearch = '';
  readonly fleetSegmentSearchThreshold = 8;
  selectedHomeFromDate = '';
  selectedHomeToDate = '';
  selectedHomeSegment = 'All Segments';
  homeSegmentOptions: string[] = ['All Segments'];
  benchmarkingSegments: { key: string, value: string[] }[] = [];
  dashboardLoadError = false;
  hasPredictionData = false;
  private rawTrackerRecords: any[] = [];
  private trackerRecords: any[] = [];
  private totalsSubscription?: Subscription;
  private hadActiveDateFilter = false;
  private initialTotalVessels = 0;
  private initialTransit = 0;
  private initialLoadPort = 0;
  private initialDischargePort = 0;

  // ------------ Dashboard cards ------------
  isLoadingStats: boolean = true;
  selectedClient: string = '';
  selectedClients: string[] = [];
  clientDropdownOpen: boolean = false;

  clientList: string[] = [];
  clientSearch: string = '';
  filteredClientList: string[] = [];
  totalVessels: any;
  activeVessels: any;
  inactiveVessels: any;
  underPerformanceTcp: any;
  underPerformanceDesign: any;
  idel: any;

  cargoOperationsCount: any;
  transit: any;
  pcChart!: Chart;
  pcBenchmarkingChart!: Chart;
  tcpBenchmarkingChart!: Chart;
  isLoadingPcChart: boolean = true;
  isLoadingTcpChart: boolean = true;
  graphPreviewType: 'pc' | 'tcp' | null = null;
  previewChart?: Chart;
  isLoadingPreviewChart = false;
  private complianceApiData: ComplianceApiResponse | null = null;
  // PC Benchmarking Filters
  selectedPcSegment: string = 'MR';
  selectedPcOperation: string = 'Cargo Ops -Discharging';
  selectedPcPort: string = 'La Goulette';

  // New UI Filters for PC Benchmarking
  dummyDates: string[] = ['Last 7 Days', 'Last 30 Days', 'Last 3 Months', 'Year to Date', '2023', '2024'];
  filteredDates: string[] = [...this.dummyDates];
  selectedPcDates: string[] = [];
  dateSearch: string = '';

  dummyYears: string[] = ['2010', '2011', '2012', '2013', '2014', '2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024'];
  filteredYears: string[] = [...this.dummyYears];
  selectedPcYears: string[] = [];
  yearSearch: string = '';

  // TCP Benchmarking Filters
  selectedTcpSegment: string = 'MR';
  selectedTcpOperation: string = 'Loading per day';

  pcOperationOptions = [
    { label: 'Loading', value: 'loading' },
    { label: 'Discharging', value: 'discharging' },
    { label: 'Maneuvering', value: 'maneuvering' },
    { label: 'Idle', value: 'idle' }
  ];

  tcpOperationOptions = [
    { label: 'Loading', value: 'Loading per day' },
    { label: 'Discharging', value: 'Discharge per day (For non-special area)' },
    { label: 'Inerting', value: 'Inerting per day' },
    { label: 'Idle', value: 'Idle Par day' },
    { label: 'Heating Increase', value: 'heating ‐ increasing' },
    { label: 'Heating Maintain', value: 'heating ‐ maintaining' },
    { label: 'Manouevering', value: 'Manouevering per day' },
    { label: 'Tank Cleaning', value: 'Tank Cleaning per day' },
    { label: 'Crude Oil Washing', value: 'Crude Oil Washing per day' },
  ];

  totalCpOverall: number = 0;
  totalDesignOverall: number = 0;

  // ------------ Doughnut cards ------------
  percentage = 100;
  totalMT: any = 100;

  // ------------ Tooltip & hover (if needed) ------------
  showDropdown: boolean = false;
  hoverChart: boolean = false;
  ladenSelectedSegment: string = '';
  ladenSegments: string[] = ['Segment 1', 'Segment 2', 'Segment 3'];
  tooltipStates: { [key: string]: boolean } = { chart: false };
  markers: L.Marker[] = [];
  targetPercentage = 32;
  selectedSegment: string = '';
  showUnderperformanceTable = false;
  segments: string[] = [
    'MR',
    'LR1/Panamax',
    'LR2/Aframax',
    'LR3/Suezmax',
    'Handymax',
    'General Cargo',
    'Bulk',
    'VLCC'
  ];
  cargoOperation: string[] = ['Loading', 'Discharging', 'Tranit'];
  selectedCargoOperation: string = '';

  // ------------ OLD Chart.js line+bar (kept but not used in HTML) ------------
  chartData: ChartConfiguration<'bar' | 'line'>['data'] = {
    labels: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    datasets: [
      {
        type: 'bar',
        label: 'Actual Consumption',
        data: [90, 95, 70, 110, 75, 85, 60, 40, 90, 50, 85, 45],
        backgroundColor: '#0D47A1',
        borderRadius: 4,
        barPercentage: 0.6,
      },
      {
        type: 'line',
        label: 'TCP Consumption',
        data: [45, 65, 55, 70, 60, 50, 40, 35, 70, 55, 60, 50],
        borderColor: '#1E88E5',
        borderWidth: 2,
        fill: true,
        backgroundColor: 'rgba(30, 136, 229, 0.1)',
        tension: 0.3,
      },
      {
        type: 'line',
        label: 'Design Consumption',
        data: [10, 15, 12, 20, 18, 10, 12, 8, 20, 10, 14, 12],
        borderColor: '#E53935',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        borderDash: [5, 5],
      },
    ],
  };

  chartOptions: ChartOptions<'bar' | 'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Aframax Laden Vessel Performance',
        font: {
          size: 18,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 120,
        ticks: {
          stepSize: 20,
        },
      },
    },
  };

  // ------------ Doughnut chart config ------------
  doughnutChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        data: [this.percentage, this.totalMT - this.percentage],
        backgroundColor: ['#0d47a1', '#e0e0e0'],
        borderWidth: 0,
      },
    ],
  };

  doughnutChartOptions: ChartOptions<'doughnut'> = {
    cutout: '80%',
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  // ------------ NEW: PC Benchmarking Graph (Highcharts) ------------
  pcData: any[] = [];
  map!: L.Map;

  private baseLayer!: L.TileLayer;
  positions: any;
  userName: any;
  subusername: any;
  constructor(
    private router: Router,
    private pRequestFormService: PortRequestFormServiceService,
    private sharedDataService: DashboardService,
    private requestFormService: RequestFormService,
    private hostRef: ElementRef<HTMLElement>,
  ) { }

  ngAfterViewInit(): void {
    if (this.showLegacyUi) {
      return;
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.dashboardResizeObserver = new ResizeObserver(() => {
        this.scheduleHomeChartReflow();
      });
      this.dashboardResizeObserver.observe(this.hostRef.nativeElement);
    }
    this.scheduleHomeChartReflow();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleHomeChartReflow();
  }

  isDarkMode(): boolean {
    return this.requestFormService.isDarkMode();
  }

  @HostBinding('class.pc-light') get pcLight(): boolean {
    return !this.isDarkMode();
  }

  get selectPanelClass(): string {
    return this.isDarkMode()
      ? 'pc-home-select-panel--dark'
      : 'pc-home-select-panel--light';
  }

  get tooltipClass(): string {
    return this.isDarkMode()
      ? 'pc-home-tooltip'
      : 'pc-home-tooltip pc-home-tooltip--light';
  }

  get showFleetSegmentSearch(): boolean {
    return this.fleetSegmentOptions.length > this.fleetSegmentSearchThreshold;
  }

  get filteredFleetSegmentOptions(): string[] {
    const query = this.fleetSegmentSearch.trim().toLowerCase();
    if (!query) {
      return this.fleetSegmentOptions;
    }
    return this.fleetSegmentOptions.filter((segment) =>
      segment.toLowerCase().includes(query),
    );
  }

  getClientFilterLabel(): string {
    if (!this.selectedClients?.length) {
      return 'All';
    }
    if (this.selectedClients.length === 1) {
      return this.selectedClients[0];
    }
    return `${this.selectedClients.length} selected`;
  }

  retryDashboard(): void {
    this.dashboardLoadError = false;
    if (this.showLegacyUi) {
      this.loadLegacyDashboardData();
    } else {
      this.loadNewDashboardData();
    }
  }

  hasFleetData(): boolean {
    return this.fleetPerformanceRows.length > 0;
  }

  hasComplianceData(): boolean {
    return this.complianceTotalVoyages > 0;
  }

  hasPredictionChartData(): boolean {
    return this.hasPredictionData;
  }

  private getChartTheme() {
    const isDark = this.isDarkMode();
    return {
      label: isDark ? '#cbd5e1' : '#334155',
      axisLine: isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
      gridLine: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15, 23, 42, 0.06)',
      legend: isDark ? '#cbd5e1' : '#475569',
      legendHover: isDark ? '#ffffff' : '#0f172a',
      tooltipBg: isDark ? 'rgba(15, 23, 42, 0.97)' : 'rgba(255, 255, 255, 0.98)',
      tooltipBorder: isDark ? 'rgba(56, 189, 248, 0.4)' : 'rgba(59, 130, 246, 0.28)',
      tooltipText: isDark ? '#f8fafc' : '#1e293b',
      tooltipShadow: isDark ? 'rgba(0, 0, 0, 0.45)' : 'rgba(15, 23, 42, 0.12)',
    };
  }

  // ngOnInit(): void {
  //   this.userName = localStorage.getItem('username');

  //   // Dashboard counts
  //   forkJoin({
  //     // design: this.pRequestFormService.getDesignTcpData('design'),
  //     // tcp: this.pRequestFormService.getDesignTcpData('tcp'),
  //     // idle: this.pRequestFormService.getIdelManCargoData('idle'),
  //     // cargoOps: this.pRequestFormService.getIdelManCargoData('cargo-ops'),
  //     // maneuvering: this.pRequestFormService.getIdelManCargoData('maneuvering'),
  //     activeInactive: this.pRequestFormService.getActiveInactive(),
  //     //positions: this.pRequestFormService.getVesselPosition(),
  //   }).subscribe((res: any) => {

  //     this.isLoadingStats = false;
  //     this.activeVessels = res.Active;
  //     this.inactiveVessels = res.Inactive;
  //     this.totalVessels = res.total_vessels;

  //     this.cargoOperationsCount = res.cargo_ops;
  //     this.idel = res.idle;
  //     this.transit = res.transit;

  //     this.underPerformanceDesign = res.performance_against_design;
  //     this.underPerformanceTcp = res.performance_against_warranties;
  //     //this.positions = results.positions.data;


  //     // if (this.positions) {
  //     //   this.initMarkers();
  //     // }
  //   });
  //   this.sharedDataService.totals$.subscribe((totals) => {
  //     console.log('DASHBOARD RECEIVED TOTALS:', totals);
  //     this.totalCpOverall = totals.cp; //  1st missing line
  //     this.totalDesignOverall = totals.design; // already present
  //     this.percentage = totals.cp; // donut update
  //   });

  //   // PC Graph load
  //   this.initMap();
  //   this.getClients();
  //   this.loadPcBenchmarkingChart();
  //   this.loadTcpBenchmarkingChart();
  // }
  ngOnInit(): void {
    this.userName = localStorage.getItem('username');
    this.subusername = localStorage.getItem('loginPic') ?? '';
    this.getClients();

    this.totalsSubscription = this.sharedDataService.totals$.subscribe((totals) => {
      this.totalCpOverall = totals?.cp ?? 0;
      this.totalDesignOverall = totals?.design ?? 0;
      this.percentage = totals?.cp ?? 0;
    });
  }
  private getActiveInactiveParams() {
    return {
      client: this.getActiveInactiveClientParam() || 'ALL',
      from_date: this.selectedHomeFromDate || undefined,
      to_date: this.selectedHomeToDate || undefined,
      segment: this.getActiveSegmentParam() || undefined,
    };
  }
  ngOnDestroy(): void {
    this.totalsSubscription?.unsubscribe();
    this.dashboardResizeObserver?.disconnect();
    if (this.chartResizeTimer) {
      clearTimeout(this.chartResizeTimer);
      this.chartResizeTimer = null;
    }
  }

  // ------------ New home dashboard data & helpers ------------
  /** Previous Home Page bootstrap (stats cards + PC/TCP charts + map). */
  private loadLegacyDashboardData(): void {
    this.isLoadingStats = true;
    const activeInactiveParams = this.getActiveInactiveParams();

    this.pRequestFormService.getActiveInactive(activeInactiveParams).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        this.activeVessels = data?.Active ?? 0;
        this.inactiveVessels = data?.Inactive ?? 0;
        this.totalVessels = data?.total_vessels ?? 0;
        this.underPerformanceDesign = data?.discharging_port ?? data?.discharge_port ?? 0;
        this.underPerformanceTcp = data?.load_port ?? 0;
        this.cargoOperationsCount = data?.cargo_ops ?? 0;
        this.idel = data?.idle ?? 0;
        this.transit = data?.transit ?? 0;
        this.isLoadingStats = false;
      },
      error: () => {
        this.isLoadingStats = false;
      },
    });

    this.loadPcBenchmarkingChart();
    this.loadTcpBenchmarkingChart();

    // Map mounts only when legacy template is rendered.
    setTimeout(() => {
      if (!this.map && document.getElementById('pcRouteMap')) {
        this.initMap();
      }
    }, 0);
  }

  private loadNewDashboardData(): void {
    this.dashboardLoadError = false;
    this.isLoadingStats = true;
    this.isLoadingConsumption = true;
    this.isLoadingCompliance = true;
    this.isLoadingPrediction = true;

     const activeInactiveParams = this.getActiveInactiveParams();

    forkJoin({
      activeInactive: this.pRequestFormService.getActiveInactive(activeInactiveParams),
      tracker: this.pRequestFormService.getTrackerData(),
    }).subscribe({
      next: (res: any) => {
        const data = res.activeInactive?.data || res.activeInactive;

        this.activeVessels = data?.Active ?? 0;
        this.inactiveVessels = data?.Inactive ?? 0;
        this.initialDischargePort = data?.discharging_port ?? 0;
        this.initialLoadPort = data?.load_port ?? 0;
        this.initialTransit = data?.transit ?? 0;
        this.initialTotalVessels = data?.total_vessels ?? 0;

        this.rawTrackerRecords = res.tracker || [];
        this.trackerRecords = this.filterTrackerRecords(this.rawTrackerRecords);

        if (this.selectedHomeFromDate || this.selectedHomeToDate) {
          this.applyHomeDashboardFilters();
        } else {
          this.totalVessels = this.initialTotalVessels;
          this.transit = this.initialTransit;
          this.underPerformanceTcp = this.initialLoadPort;
          this.underPerformanceDesign = this.initialDischargePort;
          this.processTrackerDashboard(this.trackerRecords);
        }

        this.fetchOptimizationCompliance();

        this.lastUpdatedText =
          this.resolveLastUpdated(this.trackerRecords, data);

        this.isLoadingStats = false;
      },
      error: () => {
        this.dashboardLoadError = true;
        this.isLoadingStats = false;
        this.isLoadingConsumption = false;
        this.isLoadingCompliance = false;
        this.isLoadingPrediction = false;
      },
    });
  }

  private getActiveInactiveClientParam(): string | undefined {
    if (!this.selectedClients?.length) {
      return undefined;
    }
    return this.selectedClients.join(',');
  }

  private getActiveSegmentParam(): string | undefined {
    if (!this.selectedHomeSegment || this.selectedHomeSegment === 'All Segments') {
      return undefined;
    }
    const selectedSeg = this.benchmarkingSegments.find(s => s.key === this.selectedHomeSegment);
    if (selectedSeg && selectedSeg.value) {
      return selectedSeg.value.join(',');
    }
    return this.selectedHomeSegment;
  }

  private filterTrackerRecords(records: any[]): any[] {
    let filtered = applyUsernameClientFilter(records || [], this.userName);
    if (this.selectedClients?.length) {
      filtered = filtered.filter((row) =>
        matchesAnyClient(row?.client, this.selectedClients),
      );
    }
    if (this.selectedHomeFromDate || this.selectedHomeToDate) {
      filtered = filtered.filter((row) =>
        matchesPortCallDateRange(
          row?.arrival_date_and_time || row?.arrival_date_and_time_utc || row?.arrival,
          row?.departure_date_and_time || row?.departure_date_and_time_utc || row?.departure,
          this.selectedHomeFromDate || null,
          this.selectedHomeToDate || null,
        ),
      );
    }
    if (
      this.selectedHomeSegment &&
      this.selectedHomeSegment !== 'All Segments'
    ) {
      filtered = filtered.filter((row) =>
        matchSegmentFilter(row?.segment || '', this.selectedHomeSegment),
      );
    }
    return filtered;
  }

  private applyHomeDashboardFilters(): void {
    this.trackerRecords = this.filterTrackerRecords(this.rawTrackerRecords);
    const hasActiveDateFilter = !!(this.selectedHomeFromDate || this.selectedHomeToDate);

    if (hasActiveDateFilter) {
      const distinctVessels = new Set(
        this.trackerRecords
          .map((r) => (r?.vessel_name || r?.vessel_imo || '').trim().toLowerCase())
          .filter((v) => !!v),
      ).size;
      this.totalVessels = distinctVessels;

      let transitCount = 0;
      let loadPortCount = 0;
      let dischargePortCount = 0;

      for (const r of this.trackerRecords) {
        const op = (r?.operations || '').trim().toLowerCase();
        if (op === 'loading') {
          loadPortCount++;
        } else if (op === 'discharging') {
          dischargePortCount++;
        } else {
          transitCount++;
        }
      }

      this.transit = transitCount;
      this.underPerformanceTcp = loadPortCount;
      this.underPerformanceDesign = dischargePortCount;
    } else {
      this.totalVessels = this.initialTotalVessels;
      this.transit = this.initialTransit;
      this.underPerformanceTcp = this.initialLoadPort;
      this.underPerformanceDesign = this.initialDischargePort;
    }

    this.isLoadingConsumption = true;
    this.processTrackerDashboard(this.trackerRecords);
  }

  onHomeDateFilterChange(): void {
    if (!this.rawTrackerRecords?.length) {
      this.loadNewDashboardData();
      return;
    }

    this.applyHomeDashboardFilters();
    this.fetchOptimizationCompliance();
    this.fetchVesselStatus();
  }

  onHomeSegmentChange(segment: string): void {
    this.selectedHomeSegment = segment || '';
    if (!this.rawTrackerRecords?.length) {
      this.loadNewDashboardData();
      return;
    }

    this.applyHomeDashboardFilters();
    this.fetchOptimizationCompliance();
    this.fetchVesselStatus();
  }

  private fetchVesselStatus(): void {
    const activeInactiveParams = this.getActiveInactiveParams();
    const hasActiveDateFilter = !!(this.selectedHomeFromDate || this.selectedHomeToDate);

    this.pRequestFormService.getActiveInactive(activeInactiveParams).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        this.activeVessels = data?.Active ?? 0;
        this.inactiveVessels = data?.Inactive ?? 0;

        if (!hasActiveDateFilter) {
          this.totalVessels = data?.total_vessels ?? this.initialTotalVessels ?? 0;
          this.initialTotalVessels = this.totalVessels;
          this.underPerformanceDesign = data?.discharging_port ?? this.initialDischargePort ?? 0;
          this.initialDischargePort = this.underPerformanceDesign;
          this.underPerformanceTcp = data?.load_port ?? this.initialLoadPort ?? 0;
          this.initialLoadPort = this.underPerformanceTcp;
          this.transit = data?.transit ?? this.initialTransit ?? 0;
          this.initialTransit = this.transit;
        } else {
          if (data?.total_vessels && data.total_vessels > 0 && data.total_vessels !== this.initialTotalVessels) {
            this.totalVessels = data.total_vessels;
          }
        }

        this.lastUpdatedText = this.resolveLastUpdated(this.trackerRecords, data);
        this.isLoadingStats = false;
      },
      error: () => {
        this.isLoadingStats = false;
      },
    });
  }

  private getOptimizationComplianceParams() {
    return {
      client: this.getActiveInactiveClientParam() || 'ALL',
      from_date: this.selectedHomeFromDate || undefined,
      to_date: this.selectedHomeToDate || undefined,
      segment: this.getActiveSegmentParam() || undefined,
    };
  }
  private fetchOptimizationCompliance(): void {
    this.isLoadingCompliance = true;
    this.isLoadingPrediction = true;
    const clientParam = this.getActiveInactiveClientParam();
const params = this.getOptimizationComplianceParams();
    forkJoin({
      compliance: this.pRequestFormService.getOptimizationCompliance(params),
    }).subscribe({
      next: (res: any) => {
        this.complianceApiData = res.compliance;
        this.updateComplianceFromApi()
        this.isLoadingCompliance = false;
        this.isLoadingPrediction = false;
      },
      error: () => {
        this.isLoadingCompliance = false;
        this.isLoadingPrediction = false;
      },
    });
  }

  onComplianceLegChange(leg: ComplianceLegFilter): void {
    this.complianceLegFilter = leg;
    this.isLoadingCompliance = true;

    this.updateComplianceFromApi();

    this.isLoadingCompliance = false;
  }

  private processTrackerDashboard(records: any[]): void {
    const legSums = this.aggregateLegSums(records);
    const actualDashboardTotal =
      legSums.maneuvering.actual + legSums.cargo.actual + legSums.idle.actual;
    this.actualConsumptionMt = actualDashboardTotal > 0 ? actualDashboardTotal : null;

    const optimizedTotal =
      legSums.maneuvering.optimized + legSums.cargo.optimized + legSums.idle.optimized;
    this.optimizedConsumptionMt = optimizedTotal > 0 ? optimizedTotal : null;

    const totalOptimizationLossGain = records.reduce((sum, row) => {
      const rowTotal = this.getRowTotalOptimizationLossGain(row);
      return rowTotal > 0 ? sum + rowTotal : sum;
    }, 0);
    this.fuelSavedMt = totalOptimizationLossGain > 0 ? totalOptimizationLossGain : null;
    this.fuelSavedVsDesignPct =
      actualDashboardTotal > 0 && this.fuelSavedMt != null
        ? (totalOptimizationLossGain / actualDashboardTotal) * 100
        : null;

    this.legComparisonRows = this.buildLegComparisonRows(legSums);
    this.buildPredictionChart(records);
    this.isLoadingConsumption = false;
  }

  private getRowWarrantedLegValue(
    row: any,
    leg: 'idle' | 'cargo' | 'maneuvering',
  ): number {
    const warranted = row?.warranted_consumptions_mt_maneuvering_cargo_idle || {};
    const byLeg = {
      idle: warranted.idle_warranted_consumption_mt,
      cargo: warranted.cargo_ops_warranted_consumption_mt,
      maneuvering: warranted.maneuvering_warranted_consumption_mt,
    };
    return toSafeNumber(byLeg[leg]);
  }

  private getRowTechBaselineLegValue(
    row: any,
    leg: 'idle' | 'cargo' | 'maneuvering',
  ): number {
    const design = row?.design_consumptions_mt_maneuvering_cargo_idle || {};
    const byLeg = {
      idle: design.idle_design_consumption_mt,
      cargo: design.cargo_ops_design_consumption_mt,
      maneuvering: design.maneuvering_design_consumption_mt,
    };
    return toSafeNumber(byLeg[leg]);
  }

  private getRowOptimizedLegValue(
    row: any,
    leg: 'idle' | 'cargo' | 'maneuvering',
  ): number {
    const optMt = row?.optimization_consumption_mt;
    if (optMt && typeof optMt === 'object') {
      const legMap: Record<string, number> = {
        idle: toSafeNumber(optMt.idle),
        cargo: toSafeNumber(optMt.cargo_ops),
        maneuvering: toSafeNumber(optMt.manoeuvring ?? optMt.maneuvering),
      };
      if (legMap[leg] > 0) {
        return legMap[leg];
      }
    }
    const optimizationByLeg = {
      idle: row?.optimization_idle,
      cargo: row?.optimization_cargo_ops,
      maneuvering: row?.optimization_maneuvering ?? row?.optimization_manoeuvring,
    };
    return toSafeNumber(optimizationByLeg[leg]);
  }

  /**
   * Match the Vessel View optimization display:
   * prefer saved optimization values, then design fallbacks when inputs are blank.
   */
  private getRowOptimizedDisplayLegValue(
    row: any,
    leg: 'idle' | 'cargo' | 'maneuvering',
  ): number {
    const optMt = row?.optimization_consumption_mt;
    if (optMt && typeof optMt === 'object') {
      const raw =
        leg === 'maneuvering'
          ? optMt.manoeuvring ?? optMt.maneuvering ?? optMt.man
          : leg === 'cargo'
            ? optMt.cargo_ops
            : optMt.idle;
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    const topLevelValue =
      leg === 'maneuvering'
        ? row?.optimization_man ??
        row?.optimization_maneuvering ??
        row?.optimization_manoeuvring ??
        row?.maneuvering_design_consumption_mt
        : leg === 'cargo'
          ? row?.optimization_cargo_ops ?? row?.cargo_ops_design_consumption_mt
          : row?.optimization_idle ?? row?.idle_design_consumption_mt;
    const topLevelParsed = Number(topLevelValue);
    if (Number.isFinite(topLevelParsed)) {
      return topLevelParsed;
    }

    const designOps = row?.design_consumption_ops;
    if (designOps && typeof designOps === 'object') {
      const raw =
        leg === 'maneuvering'
          ? designOps.maneuvering ?? designOps.manoeuvring
          : leg === 'cargo'
            ? designOps.cargo_ops
            : designOps.idle;
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    const design = row?.design_consumptions_mt_maneuvering_cargo_idle || {};
    const designValue =
      leg === 'maneuvering'
        ? design.maneuvering_design_consumption_mt
        : leg === 'cargo'
          ? design.cargo_ops_design_consumption_mt
          : design.idle_design_consumption_mt;
    const designParsed = Number(designValue);
    return Number.isFinite(designParsed) ? designParsed : 0;
  }

  private getRowOptimizedDisplayTotal(row: any): number {
    return (
      this.getRowOptimizedDisplayLegValue(row, 'maneuvering') +
      this.getRowOptimizedDisplayLegValue(row, 'cargo') +
      this.getRowOptimizedDisplayLegValue(row, 'idle')
    );
  }

  /**
   * Mirrors vessel-view "Total Optimization" column:
   * Sum of (Optimization - Actual) per leg (man + cargo_ops + idle).
   */
  private getRowActualLegValue(row: any, leg: 'maneuvering' | 'cargo' | 'idle'): number | null {
    const opsKey = leg === 'maneuvering' ? 'maneuvering' : leg === 'cargo' ? 'cargo_ops' : 'idle';
    const ops = row?.actual_consumption_ops;
    if (ops && typeof ops === 'object') {
      const raw = ops[opsKey] ?? ops[leg];
      if (raw != null && raw !== '') {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    const nested = row?.actual_consumptions_mt_maneuvering_cargo_idle;
    if (nested && typeof nested === 'object') {
      const nestedKey =
        leg === 'maneuvering' ? 'maneuvering_actual_consumption_mt'
          : leg === 'cargo' ? 'cargo_ops_actual_consumption_mt'
            : 'idle_actual_consumption_mt';
      const raw = nested[nestedKey];
      if (raw != null && raw !== '') {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    return null;
  }

  private getRowTotalOptimizationLossGain(row: any): number {
    const legs: Array<'maneuvering' | 'cargo' | 'idle'> = ['maneuvering', 'cargo', 'idle'];
    let total = 0;
    let hasValue = false;
    for (const leg of legs) {
      const actual = this.getRowActualLegValue(row, leg);
      const opt = this.getRowOptimizedDisplayLegValue(row, leg);
      if (actual == null || opt === 0) continue;
      const diff = opt - actual;
      if (Number.isFinite(diff)) {
        total += diff;
        hasValue = true;
      }
    }
    return hasValue ? total : 0;
  }

  /**
   * Predicted MT for a tracker row (blue chart series).
   * Prefer optimization_* legs; fall back to design idle/cargo/maneuvering.
   */
  private getRowPredictedMt(row: any): number {
    const optimized =
      toSafeNumber(row?.optimization_idle) +
      toSafeNumber(row?.optimization_cargo_ops) +
      toSafeNumber(row?.optimization_maneuvering ?? row?.optimization_manoeuvring);
    if (optimized > 0) {
      return optimized;
    }

    const design = row?.design_consumptions_mt_maneuvering_cargo_idle || {};
    return (
      toSafeNumber(design.idle_design_consumption_mt) +
      toSafeNumber(design.cargo_ops_design_consumption_mt) +
      toSafeNumber(design.maneuvering_design_consumption_mt)
    );
  }

  /**
   * Actual MT for a tracker row (green chart series).
   * Prefer leg-wise actual; fall back to fuel-grade actual total.
   */
  private getRowActualLegTotal(row: any): number {
    const ops = row?.actual_consumptions_mt_maneuvering_cargo_idle || {};
    const legTotal =
      toSafeNumber(ops.idle_actual_consumption_mt) +
      toSafeNumber(ops.cargo_ops_actual_consumption_mt) +
      toSafeNumber(ops.maneuvering_actual_consumption_mt);
    return legTotal > 0 ? legTotal : getActualConsumptionMt(row);
  }

  private aggregateLegSums(records: any[]): {
    idle: { actual: number; optimized: number; warranted: number; techBaseline: number };
    cargo: { actual: number; optimized: number; warranted: number; techBaseline: number };
    maneuvering: {
      actual: number;
      optimized: number;
      warranted: number;
      techBaseline: number;
    };
  } {
    const sums = {
      idle: { actual: 0, optimized: 0, warranted: 0, techBaseline: 0 },
      cargo: { actual: 0, optimized: 0, warranted: 0, techBaseline: 0 },
      maneuvering: { actual: 0, optimized: 0, warranted: 0, techBaseline: 0 },
    };

    for (const row of records) {
      const ops = row?.actual_consumptions_mt_maneuvering_cargo_idle || {};
      sums.idle.actual += toSafeNumber(ops.idle_actual_consumption_mt);
      sums.cargo.actual += toSafeNumber(ops.cargo_ops_actual_consumption_mt);
      sums.maneuvering.actual += toSafeNumber(ops.maneuvering_actual_consumption_mt);

      sums.idle.optimized += this.getRowOptimizedDisplayLegValue(row, 'idle');
      sums.cargo.optimized += this.getRowOptimizedDisplayLegValue(row, 'cargo');
      sums.maneuvering.optimized += this.getRowOptimizedDisplayLegValue(
        row,
        'maneuvering',
      );

      sums.idle.warranted += this.getRowWarrantedLegValue(row, 'idle');
      sums.cargo.warranted += this.getRowWarrantedLegValue(row, 'cargo');
      sums.maneuvering.warranted += this.getRowWarrantedLegValue(row, 'maneuvering');

      sums.idle.techBaseline += this.getRowTechBaselineLegValue(row, 'idle');
      sums.cargo.techBaseline += this.getRowTechBaselineLegValue(row, 'cargo');
      sums.maneuvering.techBaseline += this.getRowTechBaselineLegValue(
        row,
        'maneuvering',
      );
    }

    return sums;
  }

  private buildLegComparisonRows(
    legSums: ReturnType<PortDashboardComponent['aggregateLegSums']>,
  ): LegComparisonRow[] {
    const rows: LegComparisonRow[] = [
      { key: 'idle', label: 'Idle', icon: 'pause_circle', ...this.toLegValues(legSums.idle) },
      {
        key: 'cargo',
        label: 'Cargo Operations',
        icon: 'directions_boat',
        ...this.toLegValues(legSums.cargo),
      },
      {
        key: 'maneuvering',
        label: 'Manoeuvring',
        icon: 'anchor',
        ...this.toLegValues(legSums.maneuvering),
      },
    ];

    const totalActual = rows.reduce((sum, row) => sum + (row.actual ?? 0), 0);
    const totalWarranted = rows.reduce((sum, row) => sum + (row.warranted ?? 0), 0);
    const totalOptimized = rows.reduce(
      (sum, row) => sum + (row.optimized ?? 0),
      0,
    );

    rows.push({
      key: 'total',
      label: 'Total',
      icon: 'functions',
      actual: totalActual > 0 ? totalActual : null,
      warranted: totalWarranted > 0 ? totalWarranted : null,
      optimized: totalOptimized > 0 ? totalOptimized : null,
      isTotal: true,
    });

    return rows;
  }

  private toLegValues(leg: {
    actual: number;
    warranted: number;
    optimized: number;
  }): {
    actual: number | null;
    warranted: number | null;
    optimized: number | null;
  } {
    return {
      actual: leg.actual > 0 ? leg.actual : null,
      warranted: leg.warranted > 0 ? leg.warranted : null,
      optimized: leg.optimized > 0 ? leg.optimized : null,
    };
  }

  get legComparisonColumnLabel(): string {
    return this.legComparisonMode === 'warranted'
      ? 'Warranted (MT)'
      : 'Optimization Consumption (MT)';
  }

  get legComparisonColumnTooltip(): string {
    return this.legComparisonMode === 'warranted'
      ? 'Warranted consumption for this operation leg.'
      : 'Optimization consumption for this operation leg (Maneuvering + Cargo Ops + Idle).';
  }

  getLegComparisonValue(row: LegComparisonRow): number | null {
    return this.legComparisonMode === 'warranted'
      ? row.warranted
      : row.optimized;
  }

  getLegDifferenceMt(row: LegComparisonRow): number | null {
    const comparison = this.getLegComparisonValue(row);
    if (row.actual == null || comparison == null) {
      return null;
    }
    // Reference minus actual: positive = savings (consumed less than planned).
    return comparison - row.actual;
  }

  getLegDifferencePct(row: LegComparisonRow): number | null {
    const diff = this.getLegDifferenceMt(row);
    if (diff == null || !row.actual) {
      return null;
    }
    return (diff / row.actual) * 100;
  }

  /** Formats leg difference with explicit + / − prefix. Positive = savings. */
  formatLegDifferenceNumber(value: number | null | undefined, decimals = 0): string {
    if (value == null || !Number.isFinite(value)) {
      return '--';
    }
    const formatted = Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    if (value > 0) {
      return `+${formatted}`;
    }
    if (value < 0) {
      return `−${formatted}`;
    }
    return formatted;
  }

  formatLegDifferencePercent(value: number | null | undefined, decimals = 2): string {
    if (value == null || !Number.isFinite(value)) {
      return '--';
    }
    const formatted = Math.abs(value).toFixed(decimals);
    if (value > 0) {
      return `+${formatted}%`;
    }
    if (value < 0) {
      return `−${formatted}%`;
    }
    return `${formatted}%`;
  }

  private scheduleHomeChartReflow(): void {
    if (this.chartResizeTimer) {
      clearTimeout(this.chartResizeTimer);
    }
    this.chartResizeTimer = setTimeout(() => {
      this.reflowHomeCharts();
      this.chartResizeTimer = null;
    }, 80);
  }

  private reflowHomeCharts(): void {
    try {
      (this.complianceChart as any)?.ref?.reflow?.();
    } catch {
      // ignore chart reflow errors during teardown
    }
    try {
      (this.predictionChart as any)?.ref?.reflow?.();
    } catch {
      // ignore chart reflow errors during teardown
    }
  }

  isPositiveSavings(value: number | null): boolean {
    return value != null && value > 0;
  }

  formatDashboardNumber(value: number | null | undefined, decimals = 0): string {
    if (value == null || !Number.isFinite(value)) {
      return '--';
    }
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  formatDashboardPercent(value: number | null | undefined, decimals = 2): string {
    if (value == null || !Number.isFinite(value)) {
      return '--';
    }
    return `${value.toFixed(decimals)}%`;
  }

  /**
   * Builds the Prediction Accuracy chart + KPIs from filtered tracker records.
   * No dedicated prediction API — values are derived client-side.
   */
  private buildPredictionChart(records: any[]): void {
    // Bucket rows by YYYY-MM using arrival → report → departure date.
    const monthly = new Map<string, { actual: number; predicted: number; total: number; matched: number }>();

    for (const row of records) {
      const dateValue = resolveRecordDate(row, [
        'arrival_date_and_time',
        'report_date',
        'departure_date_and_time',
      ]);
      if (!dateValue) {
        continue;
      }

      const parsed = new Date(dateValue);
      if (Number.isNaN(parsed.getTime())) {
        continue;
      }

      const key = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
      const bucket = monthly.get(key) || { actual: 0, predicted: 0, total: 0, matched: 0 };
      const actual = this.getRowActualLegTotal(row);
      const predicted = this.getRowPredictedMt(row);

      bucket.actual += actual;
      bucket.predicted += predicted;
      bucket.total += 1;
      if (actual > 0 && predicted > 0) {
        bucket.matched += 1;
      }
      monthly.set(key, bucket);
    }

    // Chart X-axis: last 8 months with data.
    const categories = Array.from(monthly.keys()).sort().slice(-8);
    this.hasPredictionData = categories.length > 0;
    if (!this.hasPredictionData) {
      this.predictionTrendSummary = [];
    }
    const actualSeries = categories.map((key) => monthly.get(key)?.actual ?? 0);
    const predictedSeries = categories.map((key) => monthly.get(key)?.predicted ?? 0);

    // Fleet KPIs: APE per matched row, then average → MAPE / Accuracy / Completeness.
    const errors: number[] = [];
    let matchedRows = 0;
    let totalRows = records.length;

    for (const row of records) {
      const actual = this.getRowActualLegTotal(row);
      const predicted = this.getRowPredictedMt(row);
      if (actual > 0 && predicted > 0) {
        matchedRows += 1;
        // Absolute Percentage Error for this row.
        errors.push(Math.abs((actual - predicted) / actual) * 100);
      }
    }

    if (errors.length) {
      const mape = errors.reduce((sum, value) => sum + value, 0) / errors.length;
      this.predictionKpis = {
        avgError: mape, // UI "Average Prediction Error" — same as MAPE
        mape,
        accuracy: Math.max(0, 100 - mape),
        completeness: totalRows > 0 ? (matchedRows / totalRows) * 100 : null,
      };
    } else {
      // TODO: Dedicated prediction-accuracy API not available — derived from tracker optimization fields.
      this.predictionKpis = {
        avgError: null,
        mape: null,
        accuracy: null,
        completeness: null,
      };
    }

    const theme = this.getChartTheme();
    const tooltipConfig = this.getRichTooltipConfig(false);
    const markerOutline = this.isDarkMode() ? '#0c1829' : '#ffffff';
    const monthLabels = categories.map((key) => {
      const [year, month] = key.split('-');
      const date = new Date(Number(year), Number(month) - 1, 1);
      return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    });
    // Latest Month / Actual Trend / Latest Gap chips.
    this.predictionTrendSummary = this.buildPredictionTrendSummary(
      monthLabels,
      actualSeries,
      predictedSeries,
    );

    this.predictionChart = new Chart({
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        height: null,
        spacing: [14, 16, 12, 12],
      },
      title: { text: undefined },
      credits: { enabled: false },
      exporting: { enabled: false },
      navigation: { buttonOptions: { enabled: false } },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        x: 0,
        itemStyle: { color: theme.legend, fontSize: '12px', fontWeight: '550' },
        itemMarginTop: 8,
        itemDistance: 24,
        symbolRadius: 4,
      },
      xAxis: {
        categories: monthLabels,
        lineColor: theme.axisLine,
        tickColor: theme.axisLine,
        labels: {
          style: { color: theme.label, fontSize: '11px', fontWeight: '550' },
          step: 1,
        },
      },
      yAxis: {
        title: {
          text: 'Consumption (MT)',
          style: { color: theme.label, fontSize: '12px', fontWeight: '550' },
        },
        gridLineColor: theme.gridLine,
        gridLineDashStyle: 'Dot',
        labels: { style: { color: theme.label, fontSize: '11px' } },
      },
      tooltip: {
        ...tooltipConfig,
        shared: true,
        useHTML: true,
        // Per-month hover: Prediction, Actual, Difference, Accuracy.
        formatter: function (this: any) {
          const points = this.points || [];
          const month = this.x ?? '';
          let predicted = 0;
          let actual = 0;
          points.forEach((point: any) => {
            if (point.series.name.includes('Prediction')) {
              predicted = point.y ?? 0;
            } else {
              actual = point.y ?? 0;
            }
          });
          const diff = actual - predicted;
          const accuracy =
            actual > 0 ? Math.max(0, 100 - (Math.abs(diff) / actual) * 100) : 0;
          return (
            `<div>` +
            `<strong>${month}</strong><br/>` +
            `Prediction: <b>${predicted.toLocaleString(undefined, { maximumFractionDigits: 0 })} MT</b><br/>` +
            `Actual: <b>${actual.toLocaleString(undefined, { maximumFractionDigits: 0 })} MT</b><br/>` +
            `Difference: <b>${diff.toLocaleString(undefined, { maximumFractionDigits: 0 })} MT</b><br/>` +
            `Accuracy: <b>${accuracy.toFixed(1)}%</b>` +
            `</div>`
          );
        },
      },
      series: [
        {
          type: 'line',
          name: 'Optimization Prediction (MT)', // blue line
          data: predictedSeries,
          color: '#38bdf8',
          marker: {
            enabled: true,
            radius: 5,
            symbol: 'circle',
            lineWidth: 2,
            lineColor: markerOutline,
          },
          lineWidth: 2.5,
        },
        {
          type: 'line',
          name: 'Actual Consumption (MT)', // green line
          data: actualSeries,
          color: '#34d399',
          marker: {
            enabled: true,
            radius: 5,
            symbol: 'circle',
            lineWidth: 2,
            lineColor: markerOutline,
          },
          lineWidth: 2.5,
        },
      ],
    } as any);

    this.isLoadingPrediction = false;
    this.scheduleHomeChartReflow();
  }

  /**
   * Right-panel trend chips for the newest chart month:
   * - Latest Month accuracy
   * - Actual Trend vs prior month
   * - Latest Gap (Actual − Prediction) in MT
   */
  private buildPredictionTrendSummary(
    monthLabels: string[],
    actualSeries: number[],
    predictedSeries: number[],
  ): Array<{
    label: string;
    value: string;
    tone: 'positive' | 'negative' | 'info' | 'neutral';
  }> {
    if (!monthLabels.length) {
      return [];
    }

    const lastIdx = monthLabels.length - 1;
    const prevIdx = monthLabels.length > 1 ? lastIdx - 1 : -1;
    const latestActual = actualSeries[lastIdx] ?? 0;
    const latestPredicted = predictedSeries[lastIdx] ?? 0;
    const latestDiff = latestActual - latestPredicted;
    // Month accuracy = 100 − |gap| / actual × 100
    const latestAccuracy =
      latestActual > 0
        ? Math.max(0, 100 - (Math.abs(latestDiff) / latestActual) * 100)
        : null;

    let actualTrend = '--';
    let actualTone: 'positive' | 'negative' | 'info' | 'neutral' = 'neutral';
    if (prevIdx >= 0) {
      const prevActual = actualSeries[prevIdx] ?? 0;
      const delta = latestActual - prevActual;
      if (prevActual > 0) {
        // % change in actual vs previous chart month (drop = positive tone).
        const pct = (delta / prevActual) * 100;
        actualTrend = `${delta >= 0 ? '+' : ''}${pct.toFixed(1)}% vs prior month`;
        actualTone = delta <= 0 ? 'positive' : 'negative';
      } else {
        actualTrend = this.formatDashboardNumber(latestActual);
        actualTone = 'info';
      }
    } else {
      actualTrend = this.formatDashboardNumber(latestActual);
      actualTone = 'info';
    }

    return [
      {
        label: `Latest Month (${monthLabels[lastIdx]})`,
        value:
          latestAccuracy != null
            ? `${latestAccuracy.toFixed(1)}% accuracy`
            : '--',
        tone: latestAccuracy == null ? 'neutral' : latestAccuracy >= 90 ? 'positive' : 'info',
      },
      {
        label: 'Actual Trend',
        value: actualTrend,
        tone: actualTone,
      },
      {
        label: 'Latest Gap (Actual − Prediction)',
        // Red when actual > prediction (over-consumed vs prediction).
        value: `${latestDiff >= 0 ? '+' : ''}${this.formatDashboardNumber(latestDiff)} MT`,
        tone: Math.abs(latestDiff) < 1 ? 'neutral' : latestDiff <= 0 ? 'positive' : 'negative',
      },
    ];
  }

  private processFleetPerformance(records: any[]): void {
    const segments = new Set<string>(['All Segments']);
    const byVessel = new Map<string, FleetPerformanceRow>();

    for (const row of records) {
      const shipName = row?.vessel_name || '--';
      const imo = row?.vessel_imo || '';
      const key = `${imo}|${shipName}`;
      const segment = row?.segment || '--';
      segments.add(this.formatFleetSegment(segment));

      const actual = getActualConsumptionMt(row);
      const warranted = this.getWarrantedConsumptionMt(row);
      const existing = byVessel.get(key) || {
        rank: 0,
        shipName,
        segment,
        actualMt: 0,
        warrantedMt: 0,
        differenceMt: null,
        differencePct: null,
        status: 'Neutral' as const,
      };

      existing.actualMt = (existing.actualMt ?? 0) + actual;
      existing.warrantedMt = (existing.warrantedMt ?? 0) + warranted;
      byVessel.set(key, existing);
    }

    const rows = Array.from(byVessel.values()).map((row) => {
      const actualMt = row.actualMt && row.actualMt > 0 ? row.actualMt : null;
      const warrantedMt = row.warrantedMt && row.warrantedMt > 0 ? row.warrantedMt : null;
      const differenceMt =
        actualMt != null && warrantedMt != null ? actualMt - warrantedMt : null;
      const differencePct =
        differenceMt != null && actualMt ? (differenceMt / actualMt) * 100 : null;
      const status: FleetPerformanceRow['status'] =
        differenceMt == null ? 'Neutral' : differenceMt >= 0 ? 'Better' : 'Worse';

      return {
        ...row,
        actualMt,
        warrantedMt,
        differenceMt,
        differencePct,
        status,
      };
    });

    rows.sort((a, b) => (b.differenceMt ?? 0) - (a.differenceMt ?? 0));
    this.fleetSegmentOptions = Array.from(segments).sort((a, b) => a.localeCompare(b));
    this.applyFleetSegmentFilter(rows);
    this.isLoadingFleet = false;
  }

  onFleetSegmentChange(segment: string): void {
    this.selectedFleetSegment = segment;
    const rows = this.getAllFleetRowsFromTracker();
    this.applyFleetSegmentFilter(rows);
  }

  onFleetSegmentPanelOpened(opened: boolean): void {
    if (!opened) {
      this.fleetSegmentSearch = '';
    }
  }

  onFleetSegmentSearchInput(event: Event): void {
    this.fleetSegmentSearch = (event.target as HTMLInputElement).value;
  }

  private getAllFleetRowsFromTracker(): FleetPerformanceRow[] {
    const byVessel = new Map<string, FleetPerformanceRow>();

    for (const row of this.trackerRecords) {
      const shipName = row?.vessel_name || '--';
      const imo = row?.vessel_imo || '';
      const key = `${imo}|${shipName}`;
      const actual = getActualConsumptionMt(row);
      const warranted = this.getWarrantedConsumptionMt(row);
      const existing = byVessel.get(key) || {
        rank: 0,
        shipName,
        segment: row?.segment || '--',
        actualMt: 0,
        warrantedMt: 0,
        differenceMt: null,
        differencePct: null,
        status: 'Neutral' as const,
      };
      existing.actualMt = (existing.actualMt ?? 0) + actual;
      existing.warrantedMt = (existing.warrantedMt ?? 0) + warranted;
      byVessel.set(key, existing);
    }

    return Array.from(byVessel.values())
      .map((row) => {
        const actualMt = row.actualMt && row.actualMt > 0 ? row.actualMt : null;
        const warrantedMt = row.warrantedMt && row.warrantedMt > 0 ? row.warrantedMt : null;
        const differenceMt =
          actualMt != null && warrantedMt != null ? actualMt - warrantedMt : null;
        const differencePct =
          differenceMt != null && actualMt ? (differenceMt / actualMt) * 100 : null;
        return {
          ...row,
          actualMt,
          warrantedMt,
          differenceMt,
          differencePct,
          status:
            differenceMt == null
              ? ('Neutral' as const)
              : differenceMt >= 0
                ? ('Better' as const)
                : ('Worse' as const),
        };
      })
      .sort((a, b) => (b.differenceMt ?? 0) - (a.differenceMt ?? 0));
  }

  private applyFleetSegmentFilter(rows: FleetPerformanceRow[]): void {
    const filtered =
      this.selectedFleetSegment === 'All Segments'
        ? rows
        : rows.filter((row) =>
          this.formatFleetSegment(row.segment) === this.selectedFleetSegment,
        );

    const displayRows = filtered.map((row, index) => ({
      ...row,
      rank: index + 1,
    }));

    this.fleetPerformanceRows = displayRows;
    this.fleetAverageRow = this.buildFleetAverageRow(filtered);
  }

  private buildFleetAverageRow(rows: FleetPerformanceRow[]): FleetPerformanceRow | null {
    if (!rows.length) {
      return null;
    }

    const actualValues = rows.map((row) => row.actualMt).filter((v): v is number => v != null);
    const warrantedValues = rows.map((row) => row.warrantedMt).filter((v): v is number => v != null);
    const actualMt = actualValues.length
      ? actualValues.reduce((sum, value) => sum + value, 0) / actualValues.length
      : null;
    const warrantedMt = warrantedValues.length
      ? warrantedValues.reduce((sum, value) => sum + value, 0) / warrantedValues.length
      : null;
    const differenceMt =
      actualMt != null && warrantedMt != null ? actualMt - warrantedMt : null;
    const differencePct =
      differenceMt != null && actualMt ? (differenceMt / actualMt) * 100 : null;

    return {
      rank: 0,
      shipName: 'Fleet Average',
      segment: '--',
      actualMt,
      warrantedMt,
      differenceMt,
      differencePct,
      status:
        differenceMt == null ? 'Neutral' : differenceMt >= 0 ? 'Better' : 'Worse',
      isAverage: true,
    };
  }

  private getWarrantedConsumptionMt(row: Record<string, unknown>): number {
    const warranted = row['warranted_consumptions_mt_maneuvering_cargo_idle'] as
      | Record<string, unknown>
      | undefined;
    const fromOps =
      toSafeNumber(warranted?.['maneuvering_warranted_consumption_mt']) +
      toSafeNumber(warranted?.['cargo_ops_warranted_consumption_mt']) +
      toSafeNumber(warranted?.['idle_warranted_consumption_mt']);

    if (fromOps > 0) {
      return fromOps;
    }

    const cp = row['cp_warranted_consumptions_mt'] as Record<string, unknown> | undefined;
    return (
      toSafeNumber(cp?.['cp_warranted_consumptions_mt_hsfo']) +
      toSafeNumber(cp?.['cp_warranted_consumptions_mt_vlsfo']) +
      toSafeNumber(cp?.['cp_warranted_consumptions_mt_lsgo'])
    );
  }

  formatFleetSegment(segment: string): string {
    if (!segment) {
      return '--';
    }
    const trimmed = segment.trim().toLowerCase().replace(/\s+/g, ' ');
    const match = trimmed.match(/^([a-z0-9]+)\s*(\(([^)]+)\))?$/i);
    if (match) {
      const main = match[1].toUpperCase();
      const bracket = match[3]
        ? ` (${match[3].charAt(0).toUpperCase()}${match[3].slice(1)})`
        : '';
      return main + bracket;
    }
    return segment.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  formatVesselDisplayName(name: string): string {
    if (!name) {
      return '--';
    }
    return name
      .toLowerCase()
      .split(' ')
      .filter((word) => word.trim())
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private resolveLastUpdated(records: any[], activeInactive: any): string {
    let latest = 0;

    for (const row of records) {
      const dateValue = resolveRecordDate(row, [
        'updated_at',
        'report_date',
        'arrival_date_and_time',
        'departure_date_and_time',
      ]);
      if (!dateValue) {
        continue;
      }
      const parsed = new Date(dateValue).getTime();
      if (!Number.isNaN(parsed) && parsed > latest) {
        latest = parsed;
      }
    }

    const fallback = activeInactive?.last_updated || activeInactive?.updated_at;
    if (fallback) {
      const parsed = new Date(fallback).getTime();
      if (!Number.isNaN(parsed) && parsed > latest) {
        latest = parsed;
      }
    }

    if (!latest) {
      return '--';
    }

    return `${new Date(latest).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    })}`;
  }

  getSummaryDisplay(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
      return '--';
    }
    return formatSummaryDisplay(value);
  }

  /* Legacy ngOnInit — preserved, inactive while showLegacyUi is false.
  ngOnInit(): void {
    this.userName = localStorage.getItem('username');

    forkJoin({
      activeInactive: this.pRequestFormService.getActiveInactive(),
      positions: this.pRequestFormService.getVesselPosition()
    }).subscribe((res: any) => {
      // ... map, AIS, benchmarking chart bootstrap ...
    });

    this.sharedDataService.totals$.subscribe((totals) => {
      this.totalCpOverall = totals.cp;
      this.totalDesignOverall = totals.design;
      this.percentage = totals.cp;
    });
    this.filteredClientList = this.clientList;
    this.initMap();
    this.getClients();
    this.loadPcBenchmarkingChart();
    this.loadTcpBenchmarkingChart();
  }
  */
  // getClients() {
  //   this.sharedDataService.getClients().subscribe((res: any) => {
  //     const data = res || [];

  //     this.clientList = data.map((item: any) => item.client_name);
  //   });
  // }
  // ------------ Navigation (keep as it is) ------------
  navigateVessels(tab: string) {
    this.router.navigate(['pc-dashboard', tab]);
  }
  clearClient() {
    this.clientSearch = '';
    this.filteredClientList = [...this.clientList];
    this.selectedClients = [];
    this.selectedClient = 'ALL';
    this.refreshClientScopedData();
  }

  submitClient() {
    this.refreshClientScopedData();
  }
  navigateToActiveVessels() {
    this.router.navigate(['/pc-home']);
  }
  filterClients() {
    const search = (this.clientSearch || '').toLowerCase();
    this.filteredClientList = this.clientList.filter((client) =>
      client.toLowerCase().includes(search),
    );
  }
  selectClient(client: string) {
    const exists = this.selectedClients.includes(client);
    this.selectedClients = exists
      ? this.selectedClients.filter((c) => c !== client)
      : [...this.selectedClients, client];
  }
  selectAllClients() {
    this.selectedClients = [...this.filteredClientList];
  }
  clearAllClients() {
    this.selectedClients = [];
  }
  applyClientSelection() {
    this.selectedClient =
      this.selectedClients.length > 0 ? this.selectedClients.join(',') : 'ALL';
    this.clientSearch = this.selectedClient === 'ALL' ? '' : this.selectedClient;
    this.clientDropdownOpen = false;
    this.refreshClientScopedData();
  }
  onClientSelectionChange(selected: string[]) {
    this.selectedClients = selected || [];
    this.selectedClient =
      this.selectedClients.length > 0 ? this.selectedClients.join(',') : 'ALL';
    this.clientSearch =
      this.selectedClient === 'ALL' ? '' : this.selectedClient;
    this.refreshClientScopedData();
  }
  onClientInputFocus() {
    this.clientDropdownOpen = true;
    this.filteredClientList = [...this.clientList];
  }
  onClientInputBlur() {
    setTimeout(() => {
      this.clientDropdownOpen = false;
    }, 150);
  }
  private refreshClientScopedData() {
    if (this.showLegacyUi) {
      this.loadLegacyDashboardData();
    } else {
      this.loadNewDashboardData();
    }
  }
  getClients() {
    forkJoin({
      clients: this.sharedDataService.getClients(),
      segments: this.pRequestFormService.getSegmentsName()
    }).subscribe(({ clients, segments }: any) => {

      const data = clients || [];

      const clientNames: string[] = (data as any[])
        .map((item: any) => item?.client_name)
        .filter(
          (name: unknown): name is string =>
            typeof name === 'string' && name.trim().length > 0,
        )
        .map((name: string) => name.trim());
 const uniqueClientNames = Array.from(new Set(clientNames)).sort((a, b) =>
      a.localeCompare(b),
    );

    // Mask client names for demo subuser
    if (this.subusername === 'demo') {
      this.clientList = uniqueClientNames.map(
        (_name: string, index: number) => `Client ${index + 1}`
      );
    } else {
      this.clientList = uniqueClientNames;
    }
      this.filteredClientList = [...this.clientList];

      if (segments && segments.segments) {
        this.benchmarkingSegments = segments.segments;
        this.homeSegmentOptions = ['All Segments', ...this.benchmarkingSegments.map((s: any) => s.key)];
      }

      console.log('CLIENT LIST:', this.clientList);
      this.refreshClientScopedData();
    });
  }
  openGraphPreview(chartType: 'pc' | 'tcp'): void {
    this.graphPreviewType = chartType;
    document.body.style.overflow = 'hidden';
    if (chartType === 'pc') {
      this.loadPcBenchmarkingChart(true);
    } else {
      this.loadTcpBenchmarkingChart(true);
    }
  }

  closeGraphPreview(): void {
    this.graphPreviewType = null;
    this.previewChart = undefined;
    this.isLoadingPreviewChart = false;
    document.body.style.overflow = '';
  }

  onPreviewFilterChange(chartType: 'pc' | 'tcp'): void {
    if (chartType === 'pc') {
      this.onPcFilterChange();
      return;
    }
    this.onTcpFilterChange();
  }

  private getRichTooltipConfig(isPreview: boolean): any {
    const theme = this.getChartTheme();
    return {
      backgroundColor: theme.tooltipBg,
      borderColor: theme.tooltipBorder,
      borderRadius: 10,
      borderWidth: 1,
      shadow: {
        color: theme.tooltipShadow,
        offsetX: 0,
        offsetY: 6,
        opacity: 0.35,
        width: 12,
      },
      useHTML: true,
      outside: false,
      hideDelay: 0,
      followPointer: false,
      stickOnContact: false,
      style: {
        color: theme.tooltipText,
        fontSize: isPreview ? '13px' : '11px',
      },
      padding: isPreview ? 14 : 10,
    };
  }

  private buildPcChartOptions(
    grouped: Record<string, { consumption: number; time: number }[]>,
    vesselNames: string[],
    avgValues: number[],
    overallAvg: number,
    isPreview: boolean
  ): any {
    const labelSize = isPreview ? '12px' : '10px';
    const tooltipConfig = this.getRichTooltipConfig(isPreview);
    const theme = this.getChartTheme();

    return {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        style: { fontFamily: 'Poppins, sans-serif' },
        marginTop: isPreview ? 28 : 20,
        marginBottom: isPreview ? 96 : 80,
        marginRight: isPreview ? 48 : 40,
        height: isPreview ? 520 : undefined,
        animation: { duration: 450 },
      },
      title: { text: '' },
      xAxis: {
        categories: vesselNames,
        labels: {
          style: { color: theme.label, fontSize: labelSize },
          rotation: isPreview ? -35 : -45,
        },
        lineColor: theme.axisLine,
        tickColor: theme.axisLine,
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Consumption (MT)',
          style: { color: theme.label, fontSize: labelSize },
        },
        gridLineColor: theme.gridLine,
        labels: { style: { color: theme.label, fontSize: labelSize } },
        plotLines: [{
          value: overallAvg,
          color: '#ef5350',
          dashStyle: 'Dash',
          width: isPreview ? 2.5 : 2,
          zIndex: 5,
          label: {
            text: `Fleet Avg: ${overallAvg.toFixed(2)} MT`,
            style: { color: '#ef5350', fontWeight: '600', fontSize: labelSize },
          },
        }],
      },
      legend: {
        itemStyle: { color: theme.legend, fontSize: labelSize },
        itemHoverStyle: { color: theme.legendHover },
      },
      tooltip: {
        ...tooltipConfig,
        shared: false,
        formatter: function () {
          const vessel = String(this.x ?? '');
          const data = grouped[vessel] || [];
          const avg = Number(this.y ?? 0);
          const rows = data.map((item, idx) => `
            <tr>
              <td>Operation ${idx + 1}</td>
              <td><strong>${item.consumption.toFixed(2)} MT</strong></td>
              <td>${item.time.toFixed(1)} h</td>
            </tr>
          `).join('');

          return `
            <div class="pc-chart-tooltip">
              <div class="pc-chart-tooltip__title">${vessel}</div>
              <div class="pc-chart-tooltip__metric">
                Average Consumption: <strong>${avg.toFixed(2)} MT</strong>
              </div>
              <div class="pc-chart-tooltip__metric">
                Fleet Average: <strong>${overallAvg.toFixed(2)} MT</strong>
              </div>
              ${data.length ? `
                <table class="pc-chart-tooltip__table">
                  <thead>
                    <tr><th>Ops</th><th>Consumption</th><th>Duration</th></tr>
                  </thead>
                  <tbody>${rows}</tbody>
                </table>
              ` : ''}
            </div>
          `;
        },
      },
      plotOptions: {
        column: {
          borderRadius: isPreview ? 6 : 4,
          borderWidth: 0,
          pointPadding: isPreview ? 0.12 : 0.1,
          groupPadding: isPreview ? 0.14 : 0.12,
          states: {
            hover: {
              brightness: 0.12,
              borderColor: 'rgba(125, 211, 252, 0.8)',
              borderWidth: 1,
            },
            inactive: { opacity: 0.65 },
          },
          color: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [[0, '#4fc3f7'], [1, '#2196f3']],
          },
        },
        series: {
          animation: { duration: 500 },
        },
      },
      series: [{
        name: 'Avg Consumption',
        type: 'column',
        data: avgValues,
      }],
      credits: { enabled: false },
    };
  }

  private buildTcpChartOptions(
    vesselNames: string[],
    consumptionValues: number[],
    overallAvg: number,
    isPreview: boolean
  ): any {
    const labelSize = isPreview ? '12px' : '10px';
    const tooltipConfig = this.getRichTooltipConfig(isPreview);
    const theme = this.getChartTheme();
    const tcpBars = getTcpBenchmarkBarColors(this.requestFormService.isDarkMode());

    return {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        style: { fontFamily: 'Poppins, sans-serif' },
        marginTop: isPreview ? 28 : 20,
        marginBottom: isPreview ? 96 : 80,
        marginRight: isPreview ? 48 : 40,
        height: isPreview ? 520 : undefined,
        animation: { duration: 450 },
      },
      title: { text: '' },
      xAxis: {
        categories: vesselNames,
        labels: {
          style: { color: theme.label, fontSize: labelSize },
          rotation: isPreview ? -35 : -45,
        },
        lineColor: theme.axisLine,
        tickColor: theme.axisLine,
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Consumption (MT)',
          style: { color: theme.label, fontSize: labelSize },
        },
        gridLineColor: theme.gridLine,
        labels: { style: { color: theme.label, fontSize: labelSize } },
        plotLines: [{
          value: overallAvg,
          color: '#ffd54f',
          dashStyle: 'Dash',
          width: isPreview ? 2.5 : 2,
          zIndex: 5,
          label: {
            text: `Fleet Avg: ${overallAvg.toFixed(2)} MT`,
            style: { color: '#ffd54f', fontWeight: '600', fontSize: labelSize },
          },
        }],
      },
      legend: {
        itemStyle: { color: theme.legend, fontSize: labelSize },
        itemHoverStyle: { color: theme.legendHover },
      },
      tooltip: {
        ...tooltipConfig,
        shared: false,
        formatter: function () {
          const vessel = String(this.x ?? '');
          const value = Number(this.y ?? 0);
          const delta = value - overallAvg;
          const deltaLabel = delta >= 0 ? 'above' : 'below';
          return `
            <div class="pc-chart-tooltip">
              <div class="pc-chart-tooltip__title">${vessel}</div>
              <div class="pc-chart-tooltip__metric">
                Consumption: <strong>${value.toFixed(2)} MT</strong>
              </div>
              <div class="pc-chart-tooltip__metric">
                Fleet Average: <strong>${overallAvg.toFixed(2)} MT</strong>
              </div>
              <div class="pc-chart-tooltip__delta ${delta >= 0 ? 'is-above' : 'is-below'}">
                ${Math.abs(delta).toFixed(2)} MT ${deltaLabel} average
              </div>
            </div>
          `;
        },
      },
      plotOptions: {
        column: {
          borderRadius: isPreview ? 6 : 4,
          borderWidth: 0,
          pointPadding: isPreview ? 0.12 : 0.1,
          groupPadding: isPreview ? 0.14 : 0.12,
          states: {
            hover: {
              brightness: 0.08,
              borderColor: tcpBars.hoverBorder,
              borderWidth: 1,
            },
            inactive: { opacity: 0.65 },
          },
          color: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [[0, tcpBars.gradientTop], [1, tcpBars.gradientBottom]],
          },
        },
        series: {
          animation: { duration: 500 },
        },
      },
      series: [{
        name: 'Consumption',
        type: 'column',
        data: consumptionValues,
        color: tcpBars.fill,
      }],
      credits: { enabled: false },
    };
  }
  // ------------ Hover helpers ------------
  onCardMouseEnter(cardType: string): void {
    this.tooltipStates[cardType] = true;
  }

  onCardMouseLeave(cardType: string): void {
    this.tooltipStates[cardType] = false;
  }

  onSegmentChange(type: string): void {
    console.log(`Segment changed for: ${type}`, this.ladenSelectedSegment);
  }

  onGraphClick(): void {
    console.log('Graph clicked!');
  }

  // ------------ Old Chart.js update (not used in UI now, but kept) ------------
  updateChart(event: MatSelectChange): void {
    this.selectedSegment = event.value;

    switch (this.selectedSegment) {
      case 'forecast':
        this.chartData.datasets = [
          {
            type: 'line',
            label: 'TCP Consumption',
            data: [95, 110, 85],
            borderColor: '#43A047',
            borderWidth: 2,
            fill: false,
          },
          {
            type: 'line',
            label: 'Design Consumption',
            data: [105, 115, 88],
            borderColor: '#FFA726',
            borderWidth: 2,
            fill: false,
          },
        ];
        break;

      case 'past records':
        this.chartData.datasets = [
          {
            type: 'bar',
            label: 'Actual Consumption',
            data: [100, 120, 90],
            backgroundColor: '#689BD9',
          },
        ];
        break;

      case 'both':
      default:
        this.chartData.datasets = [
          {
            type: 'bar',
            label: 'Actual Consumption',
            data: [100, 120, 90],
            backgroundColor: '#689BD9',
          },
          {
            type: 'line',
            label: 'TCP Consumption',
            data: [95, 110, 85],
            borderColor: '#43A047',
            borderWidth: 2,
            fill: false,
          },
          {
            type: 'line',
            label: 'Design Consumption',
            data: [105, 115, 88],
            borderColor: '#FFA726',
            borderWidth: 2,
            fill: false,
          },
        ];
        break;
    }
  }

  // ------------ NEW: PC Benchmarking Graph (Highcharts) ------------
  loadPcBenchmarkingChart(includePreview = false): void {
    const loadingPreview = includePreview && this.graphPreviewType === 'pc';
    this.isLoadingPcChart = !loadingPreview;
    if (loadingPreview) {
      this.isLoadingPreviewChart = true;
    }

    const payload = {
      port_name: [this.selectedPcPort.toLowerCase()],
      vessel_name: [],
      segment: [this.selectedPcSegment],
      operation: this.selectedPcOperation,
      client: this.selectedClient || 'ALL',
    };

    this.pRequestFormService
      .getPcBenchmarking(payload)
      .subscribe({
        next: (data: any) => {
          this.isLoadingPcChart = false;
          this.isLoadingPreviewChart = false;
          let pcData = data || [];
          // Hide Hanna vessel for Grace user
          if (this.subusername?.toLowerCase() === 'grace') {
            pcData = pcData.filter(
              (item: any) => String(item.vessel_imo ?? item.imo ?? item.imo_number) !== '9397456'
            );
          }
          const grouped: Record<string, { consumption: number; time: number }[]> = {};

          pcData.forEach((d: any) => {
            const vessel = d.vessel_name || 'NA';
            const consumption = Number(d?.operation_consumption) || 0;
            const time = Number(d?.time) || 0;

            if (!grouped[vessel]) {
              grouped[vessel] = [];
            }
            grouped[vessel].push({ consumption, time });
          });

          const vesselNames = Object.keys(grouped).sort();
          const avgValues = vesselNames.map((v) => {
            const arr = grouped[v];
            const total = arr.reduce((sum, item) => sum + item.consumption, 0);
            return arr.length > 0 ? total / arr.length : 0;
          });

          const overallTotal = avgValues.reduce((s, v) => s + v, 0);
          const overallAvg =
            avgValues.length > 0 ? overallTotal / avgValues.length : 0;

          this.pcBenchmarkingChart = new Chart(
            this.buildPcChartOptions(grouped, vesselNames, avgValues, overallAvg, false)
          );

          if (this.graphPreviewType === 'pc') {
            this.previewChart = new Chart(
              this.buildPcChartOptions(grouped, vesselNames, avgValues, overallAvg, true)
            );
          }
        },
        error: (err) => {
          console.error('PC Benchmarking failed', err);
          this.isLoadingPcChart = false;
          this.isLoadingPreviewChart = false;
          this.pcBenchmarkingChart = null as any;
          if (this.graphPreviewType === 'pc') {
            this.previewChart = null as any;
          }
        },
      });
  }

  loadTcpBenchmarkingChart(includePreview = false): void {
    const loadingPreview = includePreview && this.graphPreviewType === 'tcp';
    this.isLoadingTcpChart = !loadingPreview;
    if (loadingPreview) {
      this.isLoadingPreviewChart = true;
    }

    const payload = {
      vessel_class: [this.selectedTcpSegment],
      port_consumption_parameter: this.selectedTcpOperation,
    };

    this.pRequestFormService
      .getTcpBenchmarking(payload)
      .subscribe({
        next: (data: any) => {
          this.isLoadingTcpChart = false;
          this.isLoadingPreviewChart = false;
          let tcpData = data.matching_data || [];
          if (this.subusername?.toLowerCase() === 'grace') {
            tcpData = tcpData.filter(
              (item: any) => String(item.vessel_imo ?? item.imo ?? item.imo_number) !== '9397456'
            );
          }
          const sortedData = tcpData.sort((a: any, b: any) =>
            a.vessel_name.localeCompare(b.vessel_name)
          );

          const vesselNames = sortedData.map((d: any) => d.vessel_name);
          const consumptionValues = sortedData.map((d: any) => Number(d?.data?.total) || 0);

          const overallTotal = consumptionValues.reduce((s: number, v: number) => s + v, 0);
          const overallAvg = consumptionValues.length > 0 ? overallTotal / consumptionValues.length : 0;

          this.tcpBenchmarkingChart = new Chart(
            this.buildTcpChartOptions(vesselNames, consumptionValues, overallAvg, false)
          );

          if (this.graphPreviewType === 'tcp') {
            this.previewChart = new Chart(
              this.buildTcpChartOptions(vesselNames, consumptionValues, overallAvg, true)
            );
          }
        },
        error: (err) => {
          console.error('TCP Benchmarking failed', err);
          this.isLoadingTcpChart = false;
          this.isLoadingPreviewChart = false;
          this.tcpBenchmarkingChart = null as any;
          if (this.graphPreviewType === 'tcp') {
            this.previewChart = null as any;
          }
        },
      });
  }

  onPcFilterChange(): void {
    // Sync PC segment to TCP segment
    this.selectedTcpSegment = this.selectedPcSegment;

    // Sync PC operation to TCP operation where applicable
    if (this.selectedPcOperation === 'loading') {
      this.selectedTcpOperation = 'Loading per day';
    } else if (this.selectedPcOperation === 'discharging' || this.selectedPcOperation === 'Cargo Ops -Discharging') {
      this.selectedTcpOperation = 'Discharge per day (For non-special area)';
    } else if (this.selectedPcOperation === 'idle') {
      this.selectedTcpOperation = 'Idle Par day';
    }

    this.loadPcBenchmarkingChart(this.graphPreviewType === 'pc');
    this.loadTcpBenchmarkingChart(this.graphPreviewType === 'tcp');
  }

  onTcpFilterChange(): void {
    this.loadTcpBenchmarkingChart(this.graphPreviewType === 'tcp');
  }

  filterDates() {
    const search = this.dateSearch.toLowerCase();
    this.filteredDates = this.dummyDates.filter(d => d.toLowerCase().includes(search));
  }

  selectAllDates(event: Event) {
    event.stopPropagation();
    this.selectedPcDates = [...this.filteredDates];
  }

  clearAllDates(event: Event) {
    event.stopPropagation();
    this.selectedPcDates = [];
  }

  onDateDropdownChange(isOpen: boolean) {
    if (!isOpen) {
      this.dateSearch = '';
      this.filteredDates = [...this.dummyDates];
    }
  }

  filterYears() {
    const search = this.yearSearch.toLowerCase();
    this.filteredYears = this.dummyYears.filter(y => y.toLowerCase().includes(search));
  }

  selectAllYears(event: Event) {
    event.stopPropagation();
    this.selectedPcYears = [...this.filteredYears];
  }

  clearAllYears(event: Event) {
    event.stopPropagation();
    this.selectedPcYears = [];
  }

  onYearDropdownChange(isOpen: boolean) {
    if (!isOpen) {
      this.yearSearch = '';
      this.filteredYears = [...this.dummyYears];
    }
  }
  goToPcGraph() {
    this.router.navigate(['/pc-database'], {
      queryParams: {
        view: 'graph',
        type: 'pc'
      }
    });
  }

  goToTcpGraph() {
    this.router.navigate(['/pc-database'], {
      queryParams: {
        view: 'graph',
        type: 'tcp'
      }
    });
  }
  private loadPcGraph(): void {
    // Default payload – NO UI filters, sirf backend se data
    const payload = {
      port_name: ['la goulette'],
      vessel_name: [],
      segment: 'MR',
      operation: 'discharging',
      client: '',
    };

    this.pRequestFormService
      .getPcBenchmarking(payload)
      .subscribe({
        next: (data: any) => {
          this.pcData = data || [];

          // ✅ Group by vessel_name with consumption + time
          const grouped: {
            [key: string]: { consumption: number; time: number }[];
          } = {};

          this.pcData.forEach((d: any) => {
            const vessel = d.vessel_name || 'NA';
            const consumption = Number(d?.operation_consumption) || 0;
            const time = Number(d?.time) || 0;

            if (!grouped[vessel]) {
              grouped[vessel] = [];
            }
            grouped[vessel].push({ consumption, time });
          });

          const vesselNames = Object.keys(grouped).sort();

          const avgValues = vesselNames.map((v) => {
            const arr = grouped[v];
            const total = arr.reduce((sum, item) => sum + item.consumption, 0);
            return arr.length > 0 ? total / arr.length : 0;
          });

          const overallTotal = avgValues.reduce((s, v) => s + v, 0);
          const overallAvg =
            avgValues.length > 0 ? overallTotal / avgValues.length : 0;

          const groupedRef = grouped; // for closure

          this.pcChart = new Chart({
            chart: {
              type: 'column',
              backgroundColor: '#ecf3f9',
            },
            title: {
              text: 'Port Consumption Benchmarking (PC)',
            },
            xAxis: {
              categories: vesselNames,
              title: { text: 'Vessel Names' },
              labels: {
                rotation: -90,
                style: {
                  fontSize: '12px',
                  fontFamily: 'Poppins, sans-serif',
                },
              },
            },
            yAxis: {
              min: 0,
              title: { text: 'Consumption/10,000 m³/24Hrs' },
              plotLines: [
                {
                  value: overallAvg,
                  color: 'red',
                  dashStyle: 'Dash',
                  width: 2,
                  zIndex: 5,
                  label: {
                    text: `Avg: ${overallAvg.toFixed(2)} MT`,
                    style: { color: 'red', fontWeight: 'bold' },
                  },
                },
              ],
            },
            tooltip: {
              useHTML: true,
              formatter: function () {
                const vessel = (this.x as string) ?? '';
                const allVals = groupedRef[vessel] ?? [];

                let rows = allVals
                  .map(
                    (item: any, idx: number) => `
                  <tr>
                    <td style="padding:0 8px;text-align:left;">Ops ${idx + 1
                      }</td>
                    <td style="padding:0 8px;text-align:right;">
                      <b>${item.consumption.toFixed(2)} MT</b>
                    </td>
                    <td style="padding:0 8px;text-align:right;color:gray;">
                      (${item.time.toFixed(2)} hrs)
                    </td>
                  </tr>`,
                  )
                  .join('');

                const avgVal =
                  typeof this.y === 'number' ? this.y.toFixed(2) : '0.00';

                return `
              <b>${vessel}</b><br/>
              <table style="border-collapse:collapse;">
                <tr>
                  <td style="padding:0 8px;text-align:left;color:${this.color}">
                    Avg
                  </td>
                  <td style="padding:0 8px;text-align:right;">
                    <b>${avgVal} MT</b>
                  </td>
                </tr>
                ${rows}
              </table>
            `;
              },
            },
            plotOptions: {
              column: { dataLabels: { enabled: false } },
            },
            series: [
              {
                name: 'Avg Consumption',
                type: 'column',
                data: avgValues.map((val) => Number(val.toFixed(3))),
                color: '#001f3f',
              },
              {
                name: 'Average',
                type: 'line',
                color: 'red',
                dashStyle: 'Dash',
                marker: { enabled: false },
                data: new Array(vesselNames.length).fill(overallAvg),
                events: {
                  legendItemClick: function () {
                    return false;
                  },
                },
              },
            ],
            credits: { enabled: false },
          });
        },
        error: (err) => {
          console.error('PC Benchmarking failed', err);
          this.pcChart = null as any;
        },
      });
  }

  initMap(): void {
    this.map = L.map('pcRouteMap', {
      center: [34.553127, 18.048012],
      zoom: 2,
      minZoom: 2,
      zoomControl: true,
      attributionControl: false,
    });

    this.baseLayer = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    ).addTo(this.map);
  }
  initMarkers(): void {
    this.markers.forEach((marker) => marker.remove()); // Clear existing markers if any
    this.addMarkers();
  }
  goToFleetView() {
    this.router.navigate(['/pc-fleetView']);
  }
  // addMarkers(): void {
  //   const clientFilters: { [key: string]: (item: any) => boolean } = {
  //     'Eiger Shipping': (item) =>
  //       (item.client_name || '').toLowerCase().includes('client'),

  //     'Zeronorth ZN': (item) =>
  //       (item.client_name || '').toLowerCase().includes('zeronorth'),

  //     'Elite Shipping': (item) => {
  //       const name = (item.client_name || '').toLowerCase();
  //       return name.includes('elite');
  //     },

  //     'Bunge Shipping': (item) =>
  //       (item.client_name || '').toLowerCase().includes('bunge'),

  //     'Alcor Shipping': (item) =>
  //       (item.client_name || '').toLowerCase().includes('alcor chartering'),

  //     'EMZoil Shipping': (item) =>
  //       (item.client_name || '').toLowerCase().includes('emz'),
  //   };
  //   if (this.userName && this.userName !== 'Meraki - Operations') {
  //     if (clientFilters[this.userName]) {
  //       this.positions = this.positions.filter(clientFilters[this.userName]);
  //     }
  //   }
  //   const latestVessels: any = {};

  //   this.positions.forEach((item: any) => {
  //     const vessel = item.vesselName;
  //     const currentTime = new Date(item.report_timestamp).getTime();

  //     if (
  //       !latestVessels[vessel] ||
  //       new Date(latestVessels[vessel].report_timestamp).getTime() < currentTime
  //     ) {
  //       latestVessels[vessel] = item;
  //     }
  //   });

  //   const filteredPositions = Object.values(latestVessels);
  //   filteredPositions.forEach((res: any) => {
  //     const shipIcon = this.createIcon(
  //       res.vesselName.toUpperCase(),
  //       this.map.getZoom(),
  //     );

  //     const marker = L.marker([res.latitudeResult, res.longitudeResult], {
  //       icon: shipIcon,
  //     }).addTo(this.map);
  //     this.markers.push(marker); // Store marker for later updates
  //   });
  // }
  getAisData() {
    console.log('getAisData HIT');

    const imoNumbers = this.positions
      .map((item: any) => item.vesselIMO)
      .filter((imo: any) => !!imo);

    console.log('IMO SENT =>', imoNumbers);

    this.requestFormService.getkplerList(imoNumbers).subscribe({
      next: (res: any) => {
        console.log('KPLER RESPONSE =>', res);

        const aisList = res.data || [];
        const aisMap = new Map();

        aisList.forEach((item: any) => {
          if (item.AIS?.IMO) {
            aisMap.set(String(item.AIS.IMO), item.AIS);
          }
        });

        this.positions = this.positions.map((ship: any) => ({
          ...ship,
          AIS: aisMap.get(String(ship.vesselIMO)) || null
        }));

        console.log('MERGED DATA =>', this.positions);

        this.initMarkers();
      }
    });
  }

  addMarkers(): void {
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
    const positions =
      this.subusername?.toLowerCase() === 'grace'
        ? this.positions.filter(
          (item: any) => String(item.vesselIMO ?? item.imo ?? item.vessel_imo) !== '9397456'
        )
        : this.positions;
    const navStatusMap: Record<number, string> = {
      0: 'Under Way',
      1: 'At Anchor',
      2: 'Not Under Command',
      3: 'Restricted Manoeuvrability',
      5: 'Moored'
    };

    const latestVessels: any = {};

    positions.forEach((item: any) => {
      const ais = item.AIS;

      const lat = ais?.LATITUDE
        ? Number(ais.LATITUDE)
        : Number(item.latitudeResult);

      const lng = ais?.LONGITUDE
        ? Number(ais.LONGITUDE)
        : Number(item.longitudeResult);

      if (!lat || !lng) return;

      const vessel = item.vesselName;
      const time = new Date(item.report_timestamp).getTime();

      if (
        !latestVessels[vessel] ||
        new Date(latestVessels[vessel].report_timestamp).getTime() < time
      ) {
        latestVessels[vessel] = {
          ...item,
          latitudeResult: lat,
          longitudeResult: lng,
          heading: (ais?.HEADING || ais?.COG || 0) + 90
        };
      }
    });

    Object.values(latestVessels).forEach((res: any) => {
      const signalOld =
        (Date.now() - new Date(res.AIS?.TIMESTAMP || Date.now()).getTime()) >
        21600000;

      const marker = L.marker(
        [res.latitudeResult, res.longitudeResult],
        {
          icon: this.createIcon(
            res.vesselName?.toUpperCase() || 'NA',
            this.map.getZoom()
          )
        }
      );

      marker.bindTooltip(
        `
  <div style="
    width:255px;
    border-radius:18px;
    overflow:hidden;
    background:linear-gradient(145deg,#ffffff,#f8fbff);
    box-shadow:0 18px 40px rgba(15,23,42,.18);
    border:1px solid #dbeafe;
    font-family:Poppins,sans-serif;
  ">

    <!-- Header -->
    <div style="
      padding:14px;
      background:linear-gradient(135deg,#0f172a,#1d4ed8 60%,#38bdf8);
      color:white;
      position:relative;
    ">
      <div style="
        font-size:13px;
        font-weight:700;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      ">
        🚢 ${res.vesselName || 'NA'}
      </div>

      <div style="
        margin-top:5px;
        font-size:10px;
        display:inline-block;
        padding:4px 10px;
        border-radius:20px;
        background:rgba(255,255,255,.18);
        backdrop-filter:blur(10px);
      ">
        ${signalOld ? '🔴 Delayed AIS Signal' : '🟢 Live Tracking'}
      </div>
    </div>

    <!-- Body -->
    <div style="
      padding:12px 14px;
      font-size:11px;
      color:#0f172a;
    ">

      <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid #eef2f7;">
        <span style="color:#64748b;">📍 Destination</span>
        <b>${res.AIS?.DESTINATION || '-'}</b>
      </div>

      <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid #eef2f7;">
        <span style="color:#64748b;">🧭 Navigation</span>
        <b>${navStatusMap[Number(res.AIS?.NAV_STATUS)] || 'Unknown'}</b>
      </div>

      <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid #eef2f7;">
        <span style="color:#64748b;">🌊 Depth</span>
        <b>${res.AIS?.DRAUGHT || '-'} m</b>
      </div>

      <div style="padding:8px 0;border-bottom:1px solid #eef2f7;">
        <div style="color:#64748b;margin-bottom:4px;">📌 Coordinates</div>
        <b style="font-size:10px;color:#1e293b;">
          ${Number(res.latitudeResult).toFixed(3)},
          ${Number(res.longitudeResult).toFixed(3)}
        </b>
      </div>

      <div style="padding-top:8px;">
        <div style="color:#64748b;margin-bottom:4px;">🕒 UTC Time</div>
        <b style="font-size:10px;color:#1e293b;">
          ${res.AIS?.TIMESTAMP || '-'}
        </b>
      </div>

    </div>
  </div>
  `,
        {
          direction: 'top',
          sticky: true,
          opacity: 1,
          className: 'custom-vessel-tooltip'
        }
      );

      marker.addTo(this.map);
      this.markers.push(marker);
    });
  }
  getIconSize(zoomLevel: number): number {
    return Math.min(Math.max(zoomLevel * 2, 12), 40);
  }
  createIcon(shipName: string, zoomLevel: number): L.DivIcon {
    const size = this.getIconSize(zoomLevel) + 6;
    const isBallast = false;
    const hullColor = isBallast ? '#ff7a00' : '#7C2020';
    return L.divIcon({
      className: 'realistic-vessel-icon',
      html: `
            <div style="
              display:flex;
              flex-direction:column;
              align-items:center;
              transform: translateY(-8px);
            ">
      
              <!-- Realistic Ship SVG -->
               <svg
        width="${size * 1.6}"
        height="${size * 0.8}"
        viewBox="0 0 100 40"
        style="
          z-index:2;
          transform-origin: 50% 50%;
          transition: transform 0.3s linear;
          filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));
        "
      >
        <polygon points="0,20 15,5 15,35" fill="${hullColor}"/>
        <rect x="15" y="8" width="55" height="24" fill="${hullColor}"/>
        <rect x="70" y="10" width="20" height="20"
          fill="white" stroke="${hullColor}" stroke-width="2"/>
        <rect x="90" y="12" width="6" height="16" fill="${hullColor}"/>
      </svg>
      
              <!-- Ship Name Label -->
              <div style="
              margin-top: -2px;
              font-size: 10px;
              font-weight: bolder;
              color: midnightblue;
              white-space:nowrap;
              ">
                ${shipName || 'NA'}
              </div>
            </div>
          `,
      iconSize: [size + 20, size + 28],
      iconAnchor: [(size + 20) / 2, size / 2],
    });
  }

  private updateComplianceFromApi(): void {
    const data = this.complianceApiData;

    if (!data) {
      this.complianceTotalVoyages = 0;
      this.complianceSlices = [];
      this.complianceChart = undefined;
      return;
    }

    let source: ComplianceApiItem;

    switch (this.complianceLegFilter) {
      case 'cargo':
        source = data.cargo_ops;
        break;

      case 'idle':
        source = data.idle;
        break;

      case 'maneuvering':
        source = data.maneuvering;
        break;

      case 'all':
      default:
        source = data.overall;
        break;
    }

    this.complianceTotalVoyages =
      this.complianceLegFilter === 'all'
        ? data.total_voyage
        : source.achieved_target +
        source.exceeded_target +
        source.partially_followed;

    this.complianceSlices = [
      {
        key: 'achieved',
        label: 'Achieved Target',
        count: source.achieved_target,
        percent: source.achieved_target_percentage,
        color: '#34d399',
        tooltip: 'Actual Consumption <5% of Optimized Consumption',
      },
      {
        key: 'partial',
        label: 'Partially Followed',
        count: source.partially_followed,
        percent: source.partially_followed_percentage,
        color: '#fbbf24',
        tooltip: 'Actual Consumption >5% and <10% of Optimized Consumption',
      },
      {
        key: 'exceeded',
        label: 'Exceeded Target',
        count: source.exceeded_target,
        percent: source.exceeded_target_percentage,
        color: '#fb7185',
        tooltip: 'Actual Consumption >10% of Optimized Consumption',
      },
    ];

    this.createComplianceChart();
  }
  private createComplianceChart(): void {
    const chartData = this.complianceSlices
      .filter((slice) => slice.count > 0)
      .map((slice) => ({
        name: slice.label,
        y: slice.count,
        color: slice.color,
      }));

    if (!chartData.length) {
      this.complianceChart = undefined;
      return;
    }

    const theme = this.getChartTheme();

    this.complianceChart = new Chart({
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: null,
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
      },

      title: {
        text: undefined,
      },

      credits: {
        enabled: false,
      },

      tooltip: {
        backgroundColor: theme.tooltipBg,
        borderColor: theme.tooltipBorder,
        style: {
          color: theme.tooltipText,
        },
        pointFormat:
          '<b>{point.y}</b> voyages ({point.percentage:.2f}%)',
      },

      plotOptions: {
        pie: {
          innerSize: '68%',
          dataLabels: {
            enabled: false,
          },
          borderWidth: 0,
        },
      },

      series: [
        {
          type: 'pie',
          name: 'Voyages',
          data: chartData,
        },
      ],
    });

    this.scheduleHomeChartReflow();
  }
}